import React from "react";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";

export default function PrivacyPolicyPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Privacy Policy"
        path="/privacy"
        description="Learn how Eval Ninja collects, uses, and protects your personal information and player data."
      />
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Last updated: February 11, 2026
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-gray prose-headings:font-semibold prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-sky-600">
          <h2>1. Introduction</h2>
          <p>
            Eval Ninja ("we," "our," or "us") is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our web application and
            related services (the "Service").
          </p>
          <p>
            By using the Service, you agree to the collection and use of
            information in accordance with this policy.
          </p>

          <h2>2. Information We Collect</h2>
          <h3>Information You Provide</h3>
          <ul>
            <li>
              <strong>Account information:</strong> Name, email address, and
              password when you create an account or are invited to a club.
            </li>
            <li>
              <strong>Club and player data:</strong> Club name, player names,
              jersey numbers, parent/guardian email addresses, and other roster
              information you enter or import.
            </li>
            <li>
              <strong>Evaluation data:</strong> Skill scores, rankings, written
              comments, and other evaluation content created by coaches and
              evaluators.
            </li>
            <li>
              <strong>Payment information:</strong> Billing details processed
              through our third-party payment provider. We do not store full
              credit card numbers on our servers.
            </li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <ul>
            <li>
              <strong>Usage data:</strong> Pages visited, features used, and
              actions taken within the Service.
            </li>
            <li>
              <strong>Device information:</strong> Browser type, operating
              system, device type, and screen resolution.
            </li>
            <li>
              <strong>Log data:</strong> IP address, access times, and referring
              URLs.
            </li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service.</li>
            <li>
              Process evaluations and generate player reports and rankings.
            </li>
            <li>
              Send parent reports via email on behalf of club administrators.
            </li>
            <li>
              Communicate with you about your account, including service
              announcements and security alerts.
            </li>
            <li>Respond to support requests.</li>
            <li>
              Monitor and analyze usage trends to improve the user experience.
            </li>
          </ul>

          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share information:</p>
          <ul>
            <li>
              <strong>Within your club:</strong> Evaluation data is shared with
              club administrators, coordinators, and coaches according to their
              role-based permissions.
            </li>
            <li>
              <strong>Parent reports:</strong> When a club administrator sends
              parent reports, each parent receives only their own child's
              evaluation data.
            </li>
            <li>
              <strong>Service providers:</strong> We work with third-party
              providers for hosting (Cloudflare), database services (Supabase),
              and email delivery (Mailgun). These providers only access data as
              needed to perform services on our behalf.
            </li>
            <li>
              <strong>Legal requirements:</strong> We may disclose information if
              required by law or in response to valid legal process.
            </li>
          </ul>

          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your
            data, including:
          </p>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS).</li>
            <li>Secure password hashing using PBKDF2.</li>
            <li>
              Role-based access controls so users only see data relevant to
              their role.
            </li>
            <li>
              Token-based parent report links that do not require account
              creation.
            </li>
          </ul>
          <p>
            No method of transmission or storage is 100% secure. While we strive
            to protect your information, we cannot guarantee absolute security.
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active or as
            needed to provide the Service. Club administrators can delete player
            data and evaluation records at any time. If you request account
            deletion, we will remove your personal data within 30 days, except
            where retention is required by law.
          </p>

          <h2>7. Children's Privacy</h2>
          <p>
            The Service is designed for use by club administrators, coaches, and
            evaluators who are adults. Player data (including data about minors)
            is entered and managed by authorized adult club staff. We do not
            knowingly collect personal information directly from children under
            13. If you believe a child has provided us with personal information
            directly, please contact us so we can take appropriate action.
          </p>

          <h2>8. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your personal information.</li>
            <li>Object to or restrict certain processing of your data.</li>
            <li>Request a copy of your data in a portable format.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at the address
            below.
          </p>

          <h2>9. Cookies</h2>
          <p>
            We use essential cookies and local storage to maintain your
            authentication session and preferences. We do not use third-party
            advertising or tracking cookies.
          </p>

          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes by posting the updated policy on this
            page and updating the "Last updated" date. Your continued use of the
            Service after changes are posted constitutes acceptance of the
            revised policy.
          </p>

          <h2>11. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p>
            <strong>Eval Ninja</strong>
            <br />
            Email: support@evalninja.com
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
